import { Fragment, useMemo, useRef } from "react";
import { Box, TableContainer, Table, TableHead, TableRow, TableBody } from "@mui/material";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    PaginationState,
    SortingState,
    Table as ReactTable,
    TableOptions,
    useReactTable,
} from "@tanstack/react-table";
import { TableRightMobileShadowWrapper } from "src/components-mui/base/table-scrollable/components/table-right-mobile-shadow-wrapper";
import { useResetScrollOnKeyChange } from "src/components-mui/base/table-scrollable/hooks/use-reset-scroll-on-key-change";
import { useScrollToTableTop } from "src/components-mui/base/table-scrollable/hooks/use-scroll-to-table-top";
import { useTableScrollObserver } from "src/components-mui/base/table-scrollable/hooks/use-table-scroll-observer";
import { TableScrollableProvider } from "src/components-mui/base/table-scrollable/store/table-scrollable-provider";
import { TableFirstColumnWidth } from "src/components-mui/base/table-scrollable/table-scrollable.types";
import { ErrorBoundary } from "src/components-mui/common/error-boundary/error-boundary";
import { useMuiNavHeight } from "src/hooks/use-mui-nav-height";

interface IMuiTableScrollableProps<T extends object> {
    data: T[];
    // https://github.com/TanStack/table/issues/4382#issuecomment-1571563559
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: ColumnDef<T, any>[];
    sorting?: SortingState;
    onSortingChange?: OnChangeFn<SortingState>;
    manualSorting?: boolean;
    pagination?: PaginationState;
    onPaginationChange?: OnChangeFn<PaginationState>;
    paginationRender?: (table: ReactTable<T>) => JSX.Element;
    manualPagination?: boolean;
    rowCount?: number;
    firstColumnWidth?: TableFirstColumnWidth;
    scrollToTableTopOnPagination?: boolean;
    scrollToTableTopOffset?: number;
    resetScrollKey?: unknown;
}

const MuiTableScrollableInner = <T extends object>({
    data,
    columns,
    sorting,
    onSortingChange,
    manualSorting,
    pagination,
    onPaginationChange,
    paginationRender,
    manualPagination,
    rowCount,
    firstColumnWidth,
    scrollToTableTopOnPagination,
    scrollToTableTopOffset,
    resetScrollKey,
}: IMuiTableScrollableProps<T>) => {
    const { NAV_HEIGHT_VARIATION } = useMuiNavHeight();
    const containerRef = useRef<HTMLDivElement>(null);
    const { handleOnScroll } = useTableScrollObserver(containerRef, columns, firstColumnWidth);
    const scrollToTableTop = useScrollToTableTop(containerRef, scrollToTableTopOffset);
    const combinedResetScrollKey = useMemo(
        () => [pagination?.pageIndex, pagination?.pageSize, resetScrollKey],
        [pagination?.pageIndex, pagination?.pageSize, resetScrollKey]
    );
    useResetScrollOnKeyChange(containerRef, combinedResetScrollKey);

    const stateOptions = useMemo(
        () => ({
            ...(sorting && { sorting }),
            ...(pagination && { pagination }),
        }),
        [sorting, pagination]
    );

    const tableOptions: TableOptions<T> = {
        data,
        columns,
        state: stateOptions,
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: pagination && getPaginationRowModel(),
        manualSorting,
        manualPagination,
        rowCount,
    };

    if (onSortingChange) {
        tableOptions.onSortingChange = updater => {
            if (onPaginationChange && pagination && pagination.pageIndex !== 0) {
                onPaginationChange(previousPagination => ({ ...previousPagination, pageIndex: 0 }));
            }

            if (scrollToTableTopOnPagination) {
                scrollToTableTop();
            }

            onSortingChange(updater);
        };
    }

    if (onPaginationChange) {
        tableOptions.onPaginationChange = updater => {
            if (scrollToTableTopOnPagination) {
                scrollToTableTop();
            }
            onPaginationChange(updater);
        };
    }

    const table = useReactTable(tableOptions);

    return (
        <Box>
            <TableRightMobileShadowWrapper>
                <TableContainer
                    ref={containerRef}
                    onScroll={handleOnScroll}
                    sx={{
                        overflowY: ["hidden", "hidden", "unset"],
                        overflowX: ["auto", "auto", "unset"],
                        overflowAnchor: "none",
                    }}
                >
                    <Table>
                        <TableHead sx={{ position: ["static", "static", "sticky"], top: NAV_HEIGHT_VARIATION, zIndex: 2 }}>
                            {table.getHeaderGroups().map(headerGroup => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <Fragment key={header.id}>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                        </Fragment>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody>
                            {table.getRowModel().rows.map(row => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map(cell => (
                                        <Fragment key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Fragment>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TableRightMobileShadowWrapper>
            {paginationRender && paginationRender(table)}
        </Box>
    );
};

export const MuiTableScrollable = <T extends object>(props: IMuiTableScrollableProps<T>) => {
    return (
        <ErrorBoundary>
            <TableScrollableProvider>
                <MuiTableScrollableInner {...props} />
            </TableScrollableProvider>
        </ErrorBoundary>
    );
};
